import { CommonModule } from '@angular/common';
import {
  Component, ChangeDetectionStrategy, Input, Output, EventEmitter
} from '@angular/core';

import {
  DataActionCardInfo, DataActionFailedType, DataActionCardInfoType,
  DataActionCardStyle, DataActionInitTopWithInfo, DataActionInitTopWithoutInfo
} from '@design-system/components/templates/data-action-card/entities/data-action-card.types';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { GenericCardSpaces } from '@design-system/components/templates/generic-card/entities/generic-card.types';
import { IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import { DividerDsComponent } from '@design-system/components/atoms/divider/divider.component';
import { IconDsComponent } from '@design-system/components/atoms/icon/icon.component';
import { TypographyDsComponent } from '@design-system/components/atoms/typography/typography.component';
import { GenericCardDsComponent } from '@design-system/components/templates/generic-card/generic-card.component';

@Component({
  selector: 'app-ds-data-action-card',
  templateUrl: './data-action-card.component.html',
  styleUrls: ['./data-action-card.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    DividerDsComponent,
    IconDsComponent,
    TypographyDsComponent,
    GenericCardDsComponent
  ]
})
export class DataActionCardDsComponent {

  @Input() info: DataActionCardInfo[] = [];
  @Input() style = DataActionCardStyle.Primary;
  @Input() failedType = DataActionFailedType.Error;

  @Output() clickEvent: EventEmitter<unknown> = new EventEmitter<unknown>();

  public readonly fontSize = TypographyFontSize;

  public readonly iconFillColor = IconFillColor;

  public readonly genericCardSpace = GenericCardSpaces.None;

  constructor() { }

  get data(): DataActionCardInfo[] {
    return this.info?.filter(item => item?.Type === DataActionCardInfoType.Data) ?? [];
  }

  get actions(): DataActionCardInfo[] {
    return this.info?.filter(item => item?.Type === DataActionCardInfoType.Action) ?? [];
  }

  get onlyAction(): boolean {
    return !this.data?.length && !!this.actions?.length;
  }

  get onlyOneAction(): boolean {
    return this.onlyAction && this.actions?.length === 1;
  }

  get titleFontFamily(): TypographyFontFamily {
    return this.style === DataActionCardStyle.Primary ? TypographyFontFamily.SemiBold : TypographyFontFamily.Regular;
  }

  public getActionFailedType(item: DataActionCardInfo): DataActionFailedType {
    return item.ActionIsFailed ? (this.failedType ?? DataActionFailedType.Error) : null;
  }

  public getIconColor(item: DataActionCardInfo, isAction: boolean): IconFillColor {
    const defaultColor = isAction ? IconFillColor.Secondary : IconFillColor.Primary;
    const failedType = this.getActionFailedType(item);
    return item.ActionIsFailed && isAction ? (
      failedType === DataActionFailedType.Error ? IconFillColor.Error :
        failedType === DataActionFailedType.Warning ? IconFillColor.Warning : defaultColor
    ) : defaultColor;
  }

  public getFontColor(item: DataActionCardInfo, isAction: boolean, isSubtitle: boolean): TypographyColor {
    const defaultColor = isAction || isSubtitle ? TypographyColor.Secondary : TypographyColor.Primary;
    const failedType = this.getActionFailedType(item);
    return item.ActionIsFailed && isAction ? (
      failedType === DataActionFailedType.Error ? TypographyColor.Error :
        failedType === DataActionFailedType.Warning ? TypographyColor.Warning : defaultColor
    ) : defaultColor;
  }

  public topByAction(isFirst: boolean): string {
    const value = isFirst && this.onlyAction ? DataActionInitTopWithoutInfo : DataActionInitTopWithInfo;
    const top = value * -1;
    return `${top}px`;
  }

  public isFirstWithoutData(isFirst: boolean): boolean {
    return this.onlyAction && isFirst;
  }

  public trackById(index: number): number {
    return index;
  }

  public clickEventTrigger(item: DataActionCardInfo): void {
    this.clickEvent.emit(item.ActionIdentifier);
  }

}
